import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TuiAxes, TuiBar } from '@taiga-ui/addon-charts';
import { exist } from '@campaign-portal/namespace/common/id';
import { Campaign, CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import { calculateBarChartMax } from '@helpers/utils/math';
import { TrafficType } from '@campaign-portal/namespace/common/enums';
import { AlarisConfigService, AlarisLanguageService, ifStateAnimation } from '@campaign-portal/components-library';
import { CompactNumberPipe } from '@helpers/pipes/compact-number.pipe';

@Component({
	selector: 'app-campaign-details-chart',
	standalone: true,
	animations: [ifStateAnimation],
	imports: [DecimalPipe, TranslateModule, TuiAxes, TuiBar],
	providers: [CompactNumberPipe],
	templateUrl: './campaign-details-chart.component.html',
	styleUrl: './campaign-details-chart.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignDetailsChartComponent implements OnChanges {
	@Input({ required: true }) campaign!: Campaign<exist>;

	isSeenExist = false;
	axisYLabels: string[] = [];
	axisXLabels: string[] = [];

	chartMax = 0;
	chartValue: number[][] = [];
	chartHasValues = false;
	readonly horizontalLines = 4;
	readonly CampaignStatus = CampaignStatus;

	constructor(
		public readonly config: AlarisConfigService,
		private readonly compactNumber: CompactNumberPipe,
		private readonly langService: AlarisLanguageService
	) {}

	ngOnChanges(): void {
		this.chartValue = this.generateData();
		this.chartMax = calculateBarChartMax(this.chartValue);
		this.axisYLabels = this.generateAxisYLabels();
		this.axisXLabels = this.generateAxisXLabels();

		this.chartHasValues = this.chartValue.flat().some((amount) => amount > 0);
	}

	generateAxisXLabels(): string[] {
		const isViberChannel = this.campaign.setup.channels.find((c) => c.trafficType === TrafficType.VIBER);
		return isViberChannel
			? [
					this.langService.translate('enums.SENT'),
					this.langService.translate('enums.DELIVERED'),
					this.langService.translate('enums.SEEN'),
					this.langService.translate('enums.UNDELIVERED'),
					this.langService.translate('enums.FAILED'),
				]
			: [
					this.langService.translate('enums.SENT'),
					this.langService.translate('enums.DELIVERED'),
					this.langService.translate('enums.UNDELIVERED'),
					this.langService.translate('enums.FAILED'),
				];
	}

	generateAxisYLabels(): string[] {
		const step = this.chartMax / this.horizontalLines;
		if (step < 5) {
			return ['0', ...new Array(this.horizontalLines - 1).fill(''), this.chartMax.toFixed()];
		}

		const labels: string[] = [];
		for (let i = 0; i <= this.horizontalLines; i++) {
			labels.push(this.compactNumber.transform(i * step, 0));
		}
		return labels;
	}

	generateData(): number[][] {
		const isViberChannel = this.campaign.setup.channels.find((c) => c.trafficType === TrafficType.VIBER);
		const statsForViber = [
			this.campaign.statistics?.sent,
			this.campaign.statistics?.delivered,
			this.campaign.statistics?.seen,
			this.campaign.statistics?.undelivered,
			this.campaign.statistics?.failed,
		];
		const statsForSms = [
			this.campaign.statistics?.sent,
			this.campaign.statistics?.delivered,
			this.campaign.statistics?.undelivered,
			this.campaign.statistics?.failed,
		];
		this.isSeenExist = !!isViberChannel;
		const statistics = isViberChannel ? statsForViber : statsForSms;

		return statistics.reduce((chartValue, amount, index) => {
			const singleBarValue: number[] = new Array(statistics.length).fill(0);
			singleBarValue[index] = amount ?? 0;
			chartValue.push(singleBarValue);

			return chartValue;
		}, [] as number[][]);
	}
}
